import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { ErrorNote, Frame, Spinner } from '../components/ui';

const MIN_PASSWORD = 12;

/**
 * Where an administrator's setup link lands: set a password, and be signed in.
 *
 * The same page serves a brand-new account and a forgotten password, because
 * they are the same operation - only the wording differs, and it is driven by
 * whether the account has ever signed in.
 */
export function Setup() {
  const { token } = useParams({ from: '/setup/$token' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const invitation = useQuery({
    queryKey: ['setup', token],
    queryFn: () => api.setup.read(token),
    retry: false,
  });

  const claim = useMutation({
    mutationFn: () => api.setup.claim(token, password),
    onSuccess: async (result) => {
      // The response already carries a session cookie, so the app can be
      // entered directly rather than bouncing through the sign-in form.
      queryClient.clear();
      await navigate({ to: result.role === 'super_admin' ? '/enter' : '/classes' });
    },
  });

  if (invitation.isLoading) {
    return (
      <Frame title="One moment">
        <Spinner label="Checking your link" />
      </Frame>
    );
  }

  if (invitation.isError || !invitation.data) {
    return (
      <Frame
        title="This link has expired"
        subtitle="Setup links work once and are valid for a limited time."
      >
        <p className="mb-6 text-sm text-ink-soft">Ask whoever invited you to send a new one.</p>
        <Link to="/login" className="btn">
          Go to sign in
        </Link>
      </Frame>
    );
  }

  const { name, identifier, organizationName } = invitation.data;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    claim.mutate();
  };

  return (
    <Frame
      title="Choose a password"
      subtitle={
        organizationName ? `${name} · ${organizationName}` : `${name} · platform administration`
      }
    >
      <form className="grid gap-4" onSubmit={submit}>
        {mismatch ? <ErrorNote message="Those two passwords do not match." /> : null}
        {claim.error ? (
          <ErrorNote
            message={
              claim.error instanceof ApiError
                ? claim.error.message
                : 'Could not reach the server. Check your connection and try again.'
            }
          />
        ) : null}

        <label className="grid gap-1.5">
          <span className="eyebrow">Signing in as</span>
          <input className="field font-mono text-sm" value={identifier ?? name} readOnly disabled />
        </label>

        <label className="grid gap-1.5">
          <span className="eyebrow">New password</span>
          <input
            className="field"
            type="password"
            required
            autoFocus
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`at least ${MIN_PASSWORD} characters`}
          />
        </label>

        <label className="grid gap-1.5">
          <span className="eyebrow">Confirm password</span>
          <input
            className="field"
            type="password"
            required
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        <button className="btn btn-primary" type="submit" disabled={claim.isPending}>
          {claim.isPending ? 'Setting…' : 'Set password and sign in'}
        </button>
      </form>
    </Frame>
  );
}
